import React, { useState } from 'react';
import CollapseAnimation from 'components/atoms/base-animations/collapse';
import classNames from 'classnames';

import s from './styles.module.scss';
import { fieldTypeComponents } from './components/field-type-components';
import ExpandButton from '../expand-button';

import type { FormikProps } from 'formik';
import type { TFormTemplateItem, FieldValueMap, TFieldProps } from './types';

function renderField<K extends keyof FieldValueMap>(
  type: K,
  props: TFieldProps<K>,
) {
  return fieldTypeComponents[type](props);
}

interface FormItemProps<TValues, TItem extends TFormTemplateItem> {
  item: TItem;
  formik: FormikProps<TValues>;
  onChange?: (item: TItem, val: any) => void;
}

const TemplateFormItem = <
  TValues extends Record<string, any>,
  TItem extends TFormTemplateItem & { name: keyof TValues },
>({
  item,
  formik,
  onChange,
}: FormItemProps<TValues, TItem>) => {
  const { values, setFieldValue } = formik;

  const [open, setOpen] = useState(true);

  if (item.type === 'info') {
    return <div className={s.wrapper}>{item.html}</div>;
  }

  const value = values[item.name];
  const isTitleValid =
    typeof item.title === 'string' ? true : React.isValidElement(item.title);

  const handleChange = (val: FieldValueMap[typeof item.type]) => {
    const key = item.name;
    setFieldValue(key, val);
    onChange?.(item, val);
  };

  const toggleExpand = () => setOpen(!open);

  return (
    <div>
      <div
        className={classNames(s.wrapper, {
          [s.opened]: open,
        })}
      >
        {(isTitleValid || item.closable) && (
          <div
            className={classNames(s.header, {
              [s.solo]: !item.closable,
            })}
          >
            {isTitleValid && (
              <p className={s['title-wrapper']}>
                {item.title} {item.required && <span>*</span>}
              </p>
            )}
            <div>
              {item.closable && (
                <ExpandButton
                  size="small"
                  expanded={open}
                  onClick={toggleExpand}
                />
              )}
            </div>
          </div>
        )}
        <CollapseAnimation opened={open}>
          <div className={s.items}>
            {item.description && (
              <p className={s.description}>{item.description}</p>
            )}
            {renderField(item.type, {
              item,
              value,
              onChange: handleChange,
              formik,
            })}
          </div>
        </CollapseAnimation>
      </div>

      {item?.items?.map((i) => (
        <TemplateFormItem item={i} key={i.key} formik={formik} />
      ))}
    </div>
  );
};
export default TemplateFormItem;
